import { useEffect } from 'react'
import { datadogRum } from '@datadog/browser-rum'
import type { FC } from 'react'
import { Box, Grid, Paper, Typography, Button } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'

import { copyToClipboard } from '~/shared/lib/clipboard'
import theme from '~/shared/lib/theme'
import { logger } from '~/shared/lib/logger'

interface LucianErrorScreenProps {
  error: unknown
}

const LucianErrorScreen: FC<LucianErrorScreenProps> = props => {
  const { error } = props
  const message = error instanceof Error ? error.message : String(error)
  const stack = error instanceof Error ? error.stack : undefined

  // RUM error tracking does not see logger calls, so the crash is reported to it directly.
  useEffect(() => {
    datadogRum.addError(error)
    logger.error('[ErrorScreen] unhandled error', error)
  }, [error])

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          bgcolor: '#071F3E',
          position: 'absolute',
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Explicit responsive width (mirrors the old Grid columns
            xs=11/sm=7/md=6/lg=5/xl=4 of 12). A bare <Grid> without a
            <Grid container> no longer caps its width in MUI v5. */}
        <Box
          sx={{
            width: { xs: '91.67%', sm: '58.33%', md: '50%', lg: '41.67%', xl: '33.33%' },
            maxHeight: '90vh',
            overflow: 'auto',
            maxWidth: '90vh',
            position: 'absolute',
          }}
        >
          <Paper elevation={20}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h4">Oh no! An error occurred.</Typography>
              <Typography variant="h6">
                Sorry about that. Please type a description of what happened and copy/paste the
                below into the #firefly-support Slack channel and we will fix ASAP.
              </Typography>
              <Box sx={{ my: 3, p: 1, bgcolor: '#eeeeee' }}>
                <pre>
                  {message}
                  <br />
                  {stack}
                </pre>
              </Box>
              <Grid container spacing={1}>
                <Grid>
                  <Button variant="contained" color="primary" onClick={() => location.reload()}>
                    Reload
                  </Button>
                </Grid>
                <Grid>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      localStorage.clear()
                      location.reload()
                    }}
                  >
                    Clear Cache &amp; Reload
                  </Button>
                </Grid>
                <Grid>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => copyToClipboard(`${message}\n${stack ?? ''}`.trim())}
                  >
                    Copy to Clipboard
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>
      </Box>
    </ThemeProvider>
  )
}

export default LucianErrorScreen
